import React, { useCallback, useEffect, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Placeholder from '@tiptap/extension-placeholder'
import CharacterCount from '@tiptap/extension-character-count'
import TextAlign from '@tiptap/extension-text-align'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import { common, createLowlight } from 'lowlight'
import { EditorToolbar } from './EditorToolbar'
import { ImageBubbleMenu } from './ImageBubbleMenu'
import { CustomImage } from '../extensions/ImageExtension'
import { CustomLink } from '../extensions/LinkExtension'
import DOMPurify from 'dompurify'
import { apiService } from '@/services/api'

const lowlight = createLowlight(common)

interface BlogSnippet {
  id: string
  name: string
  content: string
}

interface BlogEditorProps {
  content: string
  onChange: (content: string) => void
  onImageUpload?: (file: File) => Promise<string>
  placeholder?: string
  className?: string
  disabled?: boolean
}

export const BlogEditor: React.FC<BlogEditorProps> = ({
  content,
  onChange,
  onImageUpload,
  placeholder = 'Write your article content here...',
  className = '',
  disabled = false,
}) => {
  const [snippets, setSnippets] = useState<BlogSnippet[]>([])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      CustomLink.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
      CustomImage.configure({
        HTMLAttributes: {
          class: 'max-w-full rounded-lg border border-border',
        },
        inline: false,
        allowBase64: true,
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TaskList.configure({
        HTMLAttributes: {
          class: 'not-prose pl-2',
        },
      }),
      TaskItem.configure({
        nested: true,
        HTMLAttributes: {
          class: 'flex items-start',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      CharacterCount.configure({
        limit: 50000,
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
    ],
    content,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      const sanitized = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
          'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'a', 'img', 'table', 'thead',
          'tbody', 'tr', 'th', 'td', 'hr', 'div', 'span'
        ],
        ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'target'],
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):)/i,
      })
      onChange(sanitized)
    },
  })

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      // Try to parse content as JSON (TipTap format) first
      // If it's a JSON string from the backend, parse it
      // Otherwise, treat it as HTML
      try {
        const parsed = JSON.parse(content)
        // If successfully parsed and has the TipTap doc structure, use it
        if (parsed && typeof parsed === 'object' && parsed.type === 'doc') {
          editor.commands.setContent(parsed)
        } else {
          // Not a TipTap doc, treat as HTML
          editor.commands.setContent(content)
        }
      } catch {
        // Not JSON, treat as HTML
        editor.commands.setContent(content)
      }
    }
  }, [content, editor])

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled)
    }
  }, [disabled, editor])

  // Fetch snippets on mount
  useEffect(() => {
    const fetchSnippets = async () => {
      try {
        const response = await apiService.blog.snippets.list()
        setSnippets(response as BlogSnippet[])
      } catch (error) {
        console.error('Failed to fetch snippets:', error)
      }
    }
    fetchSnippets()
  }, [])

  const handleImageUpload = useCallback(
    async (file: File, cursorPosition?: number) => {
      if (!onImageUpload) {
        console.warn('No image upload handler provided')
        return
      }

      try {
        const url = await onImageUpload(file)
        if (editor) {
          // Insert the image at the saved cursor position or current position
          if (cursorPosition !== undefined) {
            editor.chain().focus().setTextSelection(cursorPosition).setImage({ src: url, width: '100%' }).run()
          } else {
            editor.chain().focus().setImage({ src: url, width: '100%' }).run()
          }
        }
      } catch (error) {
        console.error('Failed to upload image:', error)
      }
    },
    [editor, onImageUpload]
  )

  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      const items = event.clipboardData?.items
      if (!items) return

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          event.preventDefault()
          const file = items[i].getAsFile()
          if (file && editor) {
            // Save cursor position before async upload
            const { from } = editor.state.selection
            handleImageUpload(file, from)
          }
          return
        }
      }
    },
    [handleImageUpload, editor]
  )

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      const files = event.dataTransfer?.files
      if (!files || files.length === 0) return

      for (let i = 0; i < files.length; i++) {
        if (files[i].type.startsWith('image/')) {
          event.preventDefault()
          if (editor) {
            // Save cursor position before async upload
            const { from } = editor.state.selection
            handleImageUpload(files[i], from)
          }
          return
        }
      }
    },
    [handleImageUpload, editor]
  )

  const wordCount = editor?.storage.characterCount.words() || 0
  const charCount = editor?.storage.characterCount.characters() || 0
  const readingTime = Math.ceil(wordCount / 200)

  return (
    <div className={`blog-editor bg-card border border-border rounded-lg ${className}`}>
      <EditorToolbar editor={editor} onImageUpload={handleImageUpload} snippets={snippets} />
      <div className="relative bg-background">
        <ImageBubbleMenu editor={editor} />
        <EditorContent
          editor={editor}
          className="prose prose-sm dark:prose-invert max-w-none p-4 min-h-[400px] focus:outline-none"
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        />
      </div>
      <div className="border-t border-border px-4 py-2 text-sm text-muted-foreground flex items-center justify-between bg-card">
        <div className="flex gap-4">
          <span>{wordCount} words</span>
          <span>{charCount}/50,000 characters</span>
        </div>
        <span>~{readingTime} min read</span>
      </div>
    </div>
  )
}